import { EventEmitter, Injectable } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { DialogComponent } from '@shared/components/dialog/dialog.component';
import { combineLatest, map, Observable, of, take, takeUntil } from 'rxjs';
import { SingleproductComponent } from '@/features/home/components/singleproduct/singleproduct.component';
import { CART_FIND_BY_ID_SUCCESS } from '@store/actions';
import { MenuItem, Price, ProductChannel, WorkDay } from '@core/models';
import { Store } from '@ngrx/store';
import { CartItemAvailabilityStatusEnum, ComponentEnum, ModalCssClassEnum } from '@core/enums';
import { I18nService, IntegerPrecisionService } from '@core/services';
import {
	selectLanguaje,
	selectAvailabilityList,
	selectLocalChannelSesion,
	selectStore,
	selectWorkDay,
} from '@store/selectors';
import { cartCalculationProcessor, getCartItemAvailabilityStatus } from '@core/domain/cart';
import {
	ICardItemsComponent,
	ICardItemsPresenter,
	ICartItemChange,
	ICartItemTotal,
} from './interfaces';
import { IAbstractPresenter, ILanguageObject } from '@core/interfaces';
import { CartProcessorService } from '@/features/shoppingcart/domain/cart-processor.service';

@Injectable({ providedIn: 'root' })
export class CartsPresenter extends IAbstractPresenter<ICardItemsComponent> implements ICardItemsPresenter {
	no_image: string = 'assets/plato-no-image.svg';
	hoveredIndex: number | null = null;
	detailsOpenIndex: number | null = null;
	image: string | null = null;
	workDay$: Observable<WorkDay | null>;
	translations: ILanguageObject;
	decimalPrecision: string = '1.2-2';
	cartItemsWithTotals: ICartItemTotal[] = [];

	cartItems$: Observable<MenuItem[] | null> = of([]);
	showSummary: boolean = false;
	currentStep: number = 1;

	itemDeleted: EventEmitter<ICartItemChange> = new EventEmitter<ICartItemChange>();
	itemIncreased: EventEmitter<ICartItemChange> = new EventEmitter<ICartItemChange>();
	itemDecreased: EventEmitter<ICartItemChange> = new EventEmitter<ICartItemChange>();
	itemChanged: EventEmitter<ICartItemChange> = new EventEmitter<ICartItemChange>();

	constructor(
		private modalCtrl: ModalController,
		private store: Store,
		private i18nService: I18nService,
		private integerPrecisionService: IntegerPrecisionService,
		private cartProcessor: CartProcessorService,
	) {
		super();

		this.workDay$ = this.store.select(selectWorkDay);
		this.translations = this.i18nService.getTranslations(ComponentEnum.CartItems);
	}

	dispatchActions(): void {}

	suscribeObservers(): void {
		this.store
			.select(selectLanguaje)
			.pipe(takeUntil(this.destroy$))
			.subscribe(lang => {
				this.view.translations = this.i18nService.getTranslations(ComponentEnum.CartItems, lang);
			});

		this.store
			.select(selectStore)
			.pipe(takeUntil(this.destroy$))
			.subscribe(store => {
				this.view.decimalPrecision = store?.isArgentina == true ? '1.0-2' : '1.2-2';
			});

		combineLatest([
			this.view.cartItems$,
			this.workDay$,
			this.store.select(selectAvailabilityList),
			this.store.select(selectLocalChannelSesion),
		])
			.pipe(takeUntil(this.destroy$))
			.subscribe(([items, workDay, availabilityList, localChannelSession]) => {
				if (
					!items ||
					!workDay ||
					!Number.isInteger(workDay.precisionDecimal) ||
					workDay.precisionDecimal < 0
				) {
					return;
				}

				this.view.cartItemsWithTotals = items.map(item => {
					const { total, totalTaxes } = this.getTotalPrice(item, workDay);
					const availabilityStatus = getCartItemAvailabilityStatus({
						item,
						cartItems: items,
						availabilityList,
						localChannelSession,
					});

					return {
						item,
						total,
						totalTaxes,
						availabilityStatus,
					};
				});
			});
	}

	async deleteItem(item: MenuItem, index: number): Promise<void> {
		const modal = await this.modalCtrl.create({
			component: DialogComponent,
			componentProps: {
				title: this.view.translations['removeItem'],
				message: this.view.translations['removeItemQuestion'],
				type: 'warning',
				showConfirm: true,
				confirmText: 'Ok',
				isStandaloneOverlay: false,
			},
			cssClass: 'custom-overlay-pane-class',
		});

		await modal.present();

		const topModal = await this.modalCtrl.getTop();
		if (topModal) {
			const { role } = await modal.onDidDismiss();
			if (role === 'confirm') {
				this.view.itemDeleted.emit({ item, index });
			}
		}
	}

	changeAmount(item: MenuItem, value: number): void {
		this.view.itemChanged.emit({ item, value });
	}

	increaseAmount(item: MenuItem): void {
		this.view.itemIncreased.emit({ item });
	}

	decreaseAmount(item: MenuItem): void {
		this.view.itemDecreased.emit({ item });
	}

	onImgError(event: Event): void {
		(event.target as HTMLImageElement).src = this.no_image;
	}

	async editItem(item: MenuItem): Promise<void> {
		if (item)
			this.store.dispatch(
				CART_FIND_BY_ID_SUCCESS({
					cart: { ...item, cantidad: BigInt(item.cantidadProducto ?? Number(item.cantidad ?? 1n)) },
				})
			);

		const isLongMode =
			(item?.gruposModificadores?.length || 0) > 0 || (item?.productosCanal?.[0]?.precios?.length || 0) > 1;
		const cssClass = isLongMode
			? ModalCssClassEnum.CommonLong
			: ModalCssClassEnum.CommonMedium;

		const modal = await this.modalCtrl.create({
			component: SingleproductComponent,
			componentProps: {
				isEditing: true,
			},
			cssClass: cssClass,
			backdropDismiss: false,
		});

		await modal.present();

		const { data } = await modal.onDidDismiss();
		if (data && item) {
			item = { ...item, ...data };
			item.cantidadProducto = Number(item?.cantidad ?? 0n);
		}
	}

	getTotalPrice(item: MenuItem, workDay: WorkDay): { total: bigint; totalTaxes: bigint } {
		const { menuItems: [menuItem] } = this.cartProcessor.calculateCartsWithSubtotalPrice([item]);
		const { subtotal, taxes } = cartCalculationProcessor.calculate({
			cartItems: [menuItem],
			coupon: null,
			deliveryZone: null,
			orderType: null,
			tipDetail: null,
			precisionService: this.integerPrecisionService,
			workDay,
		}).cartTotals;

		return { total: subtotal + taxes, totalTaxes: taxes };
	}

	trackById(_: number, item: MenuItem): string {
		return item.idCartItem;
	}

	trackByItemId(_: number, itemWithTotal: { item: MenuItem; total: bigint; totalTaxes: bigint }): string {
		return itemWithTotal.item.idCartItem;
	}

	isMinQty(item: MenuItem): boolean {
		return (item.cantidad ?? 0n) === 1n;
	}

	attributeProduct<T extends keyof MenuItem>(key: T, item: MenuItem): Observable<MenuItem[T] | null> {
		return of(item).pipe(
			take(1),
			map(item => item?.[key] ?? null)
		);
	}

	productChannel<T extends keyof ProductChannel>(key: T, item: MenuItem): Observable<ProductChannel[T] | number | bigint | null> {
		return of(item.productosCanal).pipe(
			take(1),
			map(productosCanal => {
				const [channelProduct] = productosCanal as ProductChannel[];

				if (channelProduct?.[key] instanceof Array && key === 'precios') {
					const { precio } = (channelProduct[key]?.[0] as Price) ?? { precio: 0n };

					if (typeof precio !== 'bigint') {

						return BigInt(Number(precio) * 100)
					} else
						return precio ?? null
				}

				return channelProduct?.[key] ?? null;
			})
		);
	}

	getPrice(item: MenuItem): Observable<number | null> {
		return this.productChannel('precios', item) as Observable<number | null>
	}
}
